import { invariant } from '@vp/digital-invariant-lib';
import { useStorageRef, useStorageState } from '@vp/digital-storage-lib';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useWebAuth } from './AuthProvider';
import getLibLogger from './getLibLogger';

type InitiateActionFn<P> = (actionParameters: P) => void;
type ActionFn<P, R> = (actionParameters: P, identity: unknown) => R;
type ActionResult<R> = R | null;
type ActionError = Error | null;

// P = type of action actionParameters
// R = action result
export function useSignedInAction<P, R>(
  action: ActionFn<P, R>,
  id: string,
): [ActionResult<R>, ActionError, InitiateActionFn<P>, boolean] {
  invariant(action, 'You must provide an action function to be executed.');
  invariant(id, 'You must assign a unique action id.');
  const { current: logger } = useRef(getLibLogger());
  const count = useRef(0);
  const webAuth = useWebAuth();
  const theAction = useRef<ActionFn<P, R>>(action);

  const [result, setResult] = useState<ActionResult<R>>(null);
  const [error, setError] = useState<ActionError>(null);
  const [parameters, setParameters] = useStorageState(`${id}-parameters`, null);
  const [signInInProgress, setSignInInProgress] = useStorageRef(`${id}-sign-in-in-progress`, false);

  count.current += 1;
  logger.debug('useSignedInAction: Hook Rendered', {
    id,
    parameters,
    count,
    result,
    error,
    signInInProgress,
    isSignedIn: webAuth ? !!webAuth.isSignedIn() : 'initializing',
  });

  const initiateAction = useCallback(
    (p: P): void => {
      logger.debug('useSignedInAction: Initiating action', {
        id,
        parameters: p,
      });
      setResult(null);
      setError(null);
      setParameters(p);
    },
    // eslint-disable-next-line
    [logger, id, setParameters],
  );

  useEffect(
    () => {
      logger.debug('useSignedInAction: Executing useEffect', { id });

      const doAction = async (): Promise<void> => {
        try {
          if (!webAuth) {
            throw new Error('WebAuth is not initialized');
          }
          const identity = webAuth.getIdentity();
          const r: R = await theAction.current(parameters, identity);
          setResult(r);
        } catch (e) {
          setError(e instanceof Error ? e : new Error(String(e)));
        }
      };

      const clearState = () => {
        logger.debug('useSignedInAction: Clearing state', { id });
        setParameters(null); // clear them out
        setSignInInProgress(false);
      };

      if (result !== null || error !== null) {
        // we are done
        logger.debug('useSignedInAction: Action Complete', {
          id,
          parameters,
        });
        if (parameters) {
          clearState();
        }
      } else if (parameters) {
        // we still need to do something
        if (webAuth) {
          if (webAuth.isSignedIn()) {
            logger.debug('useSignedInAction: Doing action', {
              id,
              parameters,
            });
            doAction();
          } else if (signInInProgress) {
            // use cancelled sign-in
            logger.debug('useSignedInAction: User did not sign-in', {
              id,
              parameters,
            });
            clearState();
          } else {
            logger.debug('useSignedInAction: Signing in', {
              id,
              parameters,
            });
            setSignInInProgress(true);
            webAuth.signIn();
          }
        } else {
          logger.debug('useSignedInAction: Waiting for Auth to initialize', {
            id,
            parameters,
          });
        }
      }
    },
    // eslint-disable-next-line
    [
      id,
      webAuth,
      parameters,
      setParameters,
      error,
      setError,
      result,
      setResult,
      signInInProgress,
      setSignInInProgress,
      logger,
    ],
  );

  return [result, error, initiateAction, !!parameters];
}
